import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { zipStoredPdfs } from "../ReportGeneration/utils/pdfZip";
import { GetSessionsByInstituteCode } from "../College/API/College_APIs";
import { useInstitutes } from "../../lib/queries/lookups";
import {
  getStudentsWithMappingByInstituteId,
  getAllAssessments,
  exportSchoolDashboardExcel,
  Assessment,
} from "../StudentInformation/StudentInfo_APIs";
import { getCatalog } from "../AssessmentMapping/API/AssessmentMapping_APIs";
import {
  getReportType,
  ReportType,
} from "../UnifiedReportManagement/API/UnifiedReport_APIs";
import {
  getGeneratedReportsByInstitute,
  toggleReportVisibility,
} from "../ReportGeneration/API/GeneratedReport_APIs";
import {
  ReadAssessmentTemplates,
  GenerateUnifiedReport,
  TemplateMappingDto,
} from "../ReportTemplates/API/Report_Templates_APIs";
import { exportAssessmentDataExcel, exportPsychometricProperties, exportNavigatorProRaw, getAssessmentReportEngine } from "../NavigatorReportGeneration/API/NavigatorReportData_APIs";
import { exportMqtScoresExcel } from "../ReportGeneration/API/BetReportData_APIs";
import {
  SendReportEmail,
  SendWhatsApp,
  SendWhatsAppBulk,
} from "../ContactPerson/API/Contact_Person_APIs";
import SchoolReportModal from "../Reports/SchoolReportModal";
import MiraDesaiModal from "./components/MiraDesaiModal";
import BulkSendModal from "./components/BulkSendModal";
import EmailComposeModal from "./components/EmailComposeModal";
import DownloadsModal, { ZipJob } from "./components/DownloadsModal";
import GenerateReportsModal, { ModalStudent } from "./components/GenerateReportsModal";
import GenerateQueueModal from "./components/GenerateQueueModal";
import AutoZipModal from "./components/AutoZipModal";
import {
  uploadReportZip,
  deleteReportZip,
  AutoZipJob,
  listAutoZipJobs,
  deleteAutoZipJob,
} from "./API/ReportZip_APIs";
import { Navigator360Preview } from "./navigator360/Navigator360Report";
import { FourPagerPreview } from "./fourPager/FourPagerReport";
import PageHeader from "../../components/PageHeader";
import SearchableSelect from "../../components/SearchableSelect";
import SearchableMultiSelect from "../../components/SearchableMultiSelect";
import { useAuth, Scope } from "../../modules/auth";

// ═══════════════════════ TYPES ═══════════════════════

type StudentRow = {
  userStudentId: number;
  name: string;
  username?: string;
  email?: string;
  schoolRollNumber?: string;
  controlNumber?: number;
  phoneNumber?: string;
  studentDob?: string;
  schoolSectionId?: number;
  /** Flat student_info.student_class — Grade fallback when the section link is unset. */
  studentClass?: string | null;
  assessments?: { assessmentId: number; assessmentName: string; status: string }[];
  assignedAssessmentIds?: number[];
};

/**
 * One table row: a student paired with one of the shown assessments. A student
 * enrolled in several selected assessments therefore appears once per
 * assessment, and Status / Completed On / Report / Visible are that pairing's.
 */
type StudentAssessmentRow = {
  key: string;
  student: StudentRow;
  assessment: Assessment;
  status: string;
  completedAt?: string;
};

/** Key for the per-(assessment, student) maps and for row selection. */
const rowKey = (assessmentId: number, userStudentId: number) => `${assessmentId}:${userStudentId}`;

type SectionInfo = {
  className: string;
  sectionName: string;
  sessionId?: number;
  classId?: number;
  sectionId?: number;
};

type ReportData = {
  userStudent?: { userStudentId: number };
  reportStatus: string;
  reportUrl?: string | null;
  pdfUrl?: string | null;
  pdfStatus?: string; // notRequested | pending | rendering | ready | failed
  suppressionReason?: string | null;
  eligible?: boolean;
  [key: string]: any;
};

// Fetch a Spaces URL and force-download it with a friendly filename. Cross-origin
// <a download> is ignored by browsers, so we go via a blob (bucket CORS allows GET).
async function downloadSpacesFile(url: string, fileName: string) {
  const res = await fetch(url);
  if (!res.ok) throw new Error("fetch failed");
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl; a.download = fileName;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(objectUrl);
}

/**
 * Local calendar day of an ISO timestamp, as YYYY-MM-DD.
 *
 * The completion-date filter compares against `<input type="date">` values, which
 * are bare local dates. Reducing both sides to the same local-day key keeps the
 * range ends inclusive without any end-of-day arithmetic, and sidesteps the
 * off-by-one you get comparing a UTC instant to a local date near midnight.
 * Returns "" for null/unparseable input, which never matches a range.
 */
function localDayKey(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

/** Compact display date, e.g. "16 Jul 2026". */
function formatCompletedOn(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

const isAutoZipActive = (j: AutoZipJob) => j.status === "queued" || j.status === "running";

/** A server-side auto ZIP, shaped for the Downloads list. */
function autoZipToDownload(j: AutoZipJob): ZipJob {
  const active = isAutoZipActive(j);
  const summary = [
    `${j.added} PDF${j.added === 1 ? "" : "s"}`,
    j.parts.length > 1 ? `${j.parts.length} ZIPs` : "",
    j.missing ? `${j.missing} without a report (see _summary.csv)` : "",
    j.failed ? `${j.failed} failed to download` : "",
  ].filter(Boolean).join(" · ");
  return {
    id: `auto-${j.id}`,
    serverJobId: j.id,
    name: j.name,
    status: j.status === "done" ? "done" : j.status === "error" ? "error" : "zipping",
    phase: j.status === "queued"
      ? "Waiting for a free worker..."
      : active ? `${j.phase || "Starting..."} · ${j.processed}/${j.total}` : undefined,
    // Held under 100 until the server has stitched the last ZIP together.
    progress: active ? Math.min(99, j.total ? Math.round((j.processed / j.total) * 100) : 0) : 100,
    url: j.parts.length === 1 ? j.parts[0].url : undefined,
    downloadUrl: j.parts.length === 1 ? j.parts[0].downloadUrl : undefined,
    // Part links go out on the edge CDN; the job itself is deleted by id.
    parts: j.parts.map((p) => ({ name: p.name, url: p.downloadUrl || p.url, fileCount: p.fileCount })),
    error: j.error || undefined,
    summary,
    createdAt: j.createdAt,
  };
}

// ═══════════════════════ COMPONENT ═══════════════════════

const ReportsHubPage: React.FC = () => {
  // ── Current user (for ABAC scope-based filtering) ──
  // dhruv.kccsw@gmail.com mapped to KVS means scopes carry an `i` for that
  // institute; we filter the dropdown and the student list to honour it so the
  // user can only see what they're allotted to.
  const { currentUser } = useAuth();
  // Pull a stable reference to the scope array so downstream useMemos don't
  // re-fire every render (currentUser is a new object on each provider update,
  // but currentUser.scopes is referentially stable across renders).
  const userScopes: Scope[] = useMemo(() => currentUser?.scopes ?? [], [currentUser]);
  const isSuperAdmin = currentUser?.superAdmin === true;

  // null = no restriction (super-admin OR at least one scope row is institute-wildcard).
  const allowedInstituteIds = useMemo<Set<number> | null>(() => {
    if (isSuperAdmin) return null;
    if (!userScopes.length) return null; // no scope rows = legacy/unscoped session, don't restrict
    if (userScopes.some((s) => s.i == null)) return null;
    return new Set(userScopes.map((s) => s.i!).filter((v) => v != null));
  }, [isSuperAdmin, userScopes]);

  // ── Institute (server already scope-filters; we apply the client-side
  // allowedInstituteIds filter as a defensive belt-and-braces step) ──
  const { data: allInstitutes = [], isLoading: institutesLoading } = useInstitutes<any>();
  const institutes = useMemo(
    () =>
      allowedInstituteIds == null
        ? allInstitutes
        : allInstitutes.filter((inst: any) => allowedInstituteIds.has(Number(inst.instituteCode))),
    [allInstitutes, allowedInstituteIds]
  );
  const [selectedInstitute, setSelectedInstitute] = useState<number | "">("");

  // ── Assessment (URM approach: filtered by institute mapping) ──
  const [allAssessments, setAllAssessments] = useState<Assessment[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  /** Every assessment whose rows are on the table, in dropdown order. */
  const [selectedAssessmentIds, setSelectedAssessmentIds] = useState<number[]>([]);
  /**
   * The one assessment the bulk actions act on. The table can show several at
   * once, but generation, sending and the Excel exports are per assessment, so
   * the action bar carries a picker and ticked rows of the others are ignored.
   */
  const [actionAssessmentId, setActionAssessmentId] = useState<number | "">("");
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [mappedAssessmentIds, setMappedAssessmentIds] = useState<Set<number> | null>(null);

  // Report templates mapped to the selected assessment.
  const [templates, setTemplates] = useState<TemplateMappingDto[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | "">("");

  // ── Students ──
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [sectionLookup, setSectionLookup] = useState<Map<number, SectionInfo>>(new Map());
  const [studentsLoading, setStudentsLoading] = useState(false);

  // ── Report data ──
  /** Keyed by `${assessmentId}:${userStudentId}` — the table can show several assessments at once. */
  const [reportDataMap, setReportDataMap] = useState<Map<string, ReportData>>(new Map());
  const [reportDataLoading, setReportDataLoading] = useState(false);

  // ── Visibility ──
  /** Keyed by `${assessmentId}:${userStudentId}`, like {@link reportDataMap}. */
  const [visibilityMap, setVisibilityMap] = useState<Map<string, { id: number; visible: boolean }>>(new Map());
  const [togglingVisibility, setTogglingVisibility] = useState(false);

  // ── Selection + pagination ──
  /** Ticked rows, as `${assessmentId}:${userStudentId}` keys. */
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // ── Search + Filters ──
  const [nameQuery, setNameQuery] = useState("");
  const [usernameQuery, setUsernameQuery] = useState("");
  const [usernamePresence, setUsernamePresence] = useState<"all" | "with" | "without">("all");
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<"" | "completed" | "ongoing" | "notstarted">("");
  // Completion date range, as YYYY-MM-DD local dates. Both ends inclusive; either may stand alone.
  const [completedFrom, setCompletedFrom] = useState("");
  const [completedTo, setCompletedTo] = useState("");

  // ── Action states ──
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [modalStudents, setModalStudents] = useState<ModalStudent[]>([]);
  const [exportingMQT, setExportingMQT] = useState(false);
  const [exportingDataExcel, setExportingDataExcel] = useState(false);
  const [exportingDashboard, setExportingDashboard] = useState(false);
  const [exportingPsychometric, setExportingPsychometric] = useState(false);
  const [exportingNavigatorPro, setExportingNavigatorPro] = useState(false);
  // Structural "is this a Navigator Pro assessment" from the backend — reliable even when
  // the mapped report template isn't tagged navigator_pro (the template tag is the fallback).
  const [assessmentIsPro, setAssessmentIsPro] = useState(false);
  // downloadingZip / zipProgress removed — replaced by zipJobs

  // ── ZIP jobs (persisted across re-renders via ref+state) ──
  const [zipJobs, setZipJobs] = useState<ZipJob[]>([]);
  const zipJobsRef = useRef<ZipJob[]>(zipJobs);
  zipJobsRef.current = zipJobs;
  const [deletingJobs, setDeletingJobs] = useState<Set<string>>(new Set());
  // Server-side auto ZIPs (survive a page refresh — the server keeps the list).
  const [autoZipJobs, setAutoZipJobs] = useState<AutoZipJob[]>([]);
  const [autoZipOpen, setAutoZipOpen] = useState(false);

  // ── Modals ──
  const [downloadsOpen, setDownloadsOpen] = useState(false);
  const [zipNamePromptOpen, setZipNamePromptOpen] = useState(false);
  const [zipNameInput, setZipNameInput] = useState("");
  const pendingZipIds = useRef<number[]>([]);
  const pendingZipKind = useRef<"navigator" | "fourPager">("navigator");
  const [miraDesaiOpen, setMiraDesaiOpen] = useState(false);
  // Secret admin edit mode — unlocked by typing "boom" in the username search.
  const [adminEditMode, setAdminEditMode] = useState(false);
  const navigate = useNavigate();
  const [schoolReportOpen, setSchoolReportOpen] = useState(false);
  const [bulkSendOpen, setBulkSendOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeSending, setComposeSending] = useState(false);
  const [composeStudentIds, setComposeStudentIds] = useState<number[]>([]);
  const [composeRecipients, setComposeRecipients] = useState<{ email: string; name: string }[]>([]);
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");

  // ── Per-row states ──
  const [sendingWhatsApp, setSendingWhatsApp] = useState<Set<number>>(new Set());
  const [downloadingStudentId, setDownloadingStudentId] = useState<number | null>(null);

  // ── Navigator 360 preview ──
  const [nav360Preview, setNav360Preview] = useState<{ studentId: number; studentName: string } | null>(null);

  // ── 4-Pager preview ──
  const [fourPagerPreview, setFourPagerPreview] = useState<
    { studentId: number; studentName: string; studentClass?: string } | null
  >(null);

  // ═══════════════════════ DATA LOADING ═══════════════════════

  // Auto-select if the user is scoped to exactly one institute so they
  // don't have to pick from a single-item dropdown.
  useEffect(() => {
    if (institutes.length === 1 && selectedInstitute === "") {
      setSelectedInstitute(Number(institutes[0].instituteCode));
    }
  }, [institutes, selectedInstitute]);

  useEffect(() => {
    getAllAssessments()
      .then((res) => setAllAssessments(res.data || []))
      .catch(() => setAllAssessments([]));
  }, []);

  // Load assessment mappings for selected institute
  // null = no institute selected, empty Set = institute selected but no assessments linked
  useEffect(() => {
    if (selectedInstitute === "") { setMappedAssessmentIds(null); return; }
    setAssessmentsLoading(true);
    // Source of "this institute's assessments" = the institute_assessment catalog
    // (every mapping — institute/session/class/section — appends to it).
    getCatalog(Number(selectedInstitute))
      .then((res) => {
        const ids = new Set<number>(
          (res.data || []).filter((c: any) => c.isActive !== false).map((c: any) => Number(c.assessmentId))
        );
        setMappedAssessmentIds(ids);
      })
      .catch(() => setMappedAssessmentIds(new Set()))
      .finally(() => setAssessmentsLoading(false));
  }, [selectedInstitute]);

  // Only the assessments mapped to the selected institute are offered — an
  // institute with no mapping gets an empty list and says so, rather than the
  // whole system's catalogue.
  useEffect(() => {
    if (mappedAssessmentIds == null) { setAssessments([]); return; }
    setAssessments(allAssessments.filter((a) => mappedAssessmentIds.has(a.id)));
  }, [allAssessments, mappedAssessmentIds]);

  // Load students + sections when institute changes
  useEffect(() => {
    if (selectedInstitute === "") { setStudents([]); setSectionLookup(new Map()); return; }
    setStudentsLoading(true);
    Promise.all([
      getStudentsWithMappingByInstituteId(Number(selectedInstitute)),
      GetSessionsByInstituteCode(selectedInstitute),
    ])
      .then(([studentsRes, sessionsRes]) => {
        setStudents(studentsRes.data || []);
        // Nobody starts ticked — the user builds the selection up (the table
        // header checkbox selects all visible rows in one click when needed).
        setSelectedRowKeys(new Set());
        const lookup = new Map<number, SectionInfo>();
        for (const session of sessionsRes.data || []) {
          for (const cls of session.schoolClasses || []) {
            for (const sec of cls.schoolSections || []) {
              if (!lookup.has(sec.id)) lookup.set(sec.id, {
                className: cls.className,
                sectionName: sec.sectionName,
                sessionId: session.id,
                classId: cls.id,
                sectionId: sec.id,
              });
            }
          }
        }
        setSectionLookup(lookup);
      })
      .catch(() => { setStudents([]); setSectionLookup(new Map()); })
      .finally(() => setStudentsLoading(false));
  }, [selectedInstitute]);

  // Reset on selection change
  useEffect(() => { setSelectedAssessmentIds([]); }, [selectedInstitute]);

  /** The assessments on the table, in dropdown order. */
  const shownAssessments = useMemo(
    () => assessments.filter((a) => selectedAssessmentIds.includes(a.id)),
    [assessments, selectedAssessmentIds]
  );
  /** Stable identity for the shown set, so effects re-run on a real change only. */
  const selectedAssessmentKey = useMemo(
    () => shownAssessments.map((a) => a.id).join(","),
    [shownAssessments]
  );

  // Keep the action picker pointing at one of the selected assessments.
  useEffect(() => {
    setActionAssessmentId((prev) =>
      prev !== "" && selectedAssessmentIds.includes(Number(prev))
        ? prev
        : (selectedAssessmentIds[0] ?? ""));
  }, [selectedAssessmentIds]);
  useEffect(() => {
    // Back to the default of nobody ticked — selection is built up
    // explicitly (header checkbox = select all visible).
    setSelectedRowKeys(new Set());
    setCurrentPage(1);
    setNameQuery("");
    setSelectedGrade("");
    setSelectedSection("");
    setReportDataMap(new Map());
    setVisibilityMap(new Map());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstitute, selectedAssessmentKey]);

  // Everything that is per assessment — templates, generation, sending, the
  // Excel exports — follows the action picker rather than the table.
  const selectedAssessmentObj = useMemo(() => {
    if (actionAssessmentId === "") return null;
    return assessments.find((a) => a.id === Number(actionAssessmentId)) || null;
  }, [assessments, actionAssessmentId]);

  const reportType: ReportType = selectedAssessmentObj ? getReportType(selectedAssessmentObj) : "bet";
  const isBet = reportType === "bet";

  // Source report status/URL from the unified generated_report rows, filtered
  // to the selected template (or any, when none is selected). Keeps the rest of
  // the hub — reportStats, row preview/download, ZIP, email/WhatsApp — working
  // unchanged off reportDataMap.
  const matchesTemplate = useCallback((gr: { reportTemplateId?: number | null }) => {
    if (selectedTemplateId === "") return true;
    return gr.reportTemplateId === selectedTemplateId;
  }, [selectedTemplateId]);

  /**
   * The template dropdown belongs to the action assessment, so it only narrows
   * that assessment's reports; rows of the other shown assessments keep
   * whichever template they were generated with.
   */
  const keepReport = useCallback((assessmentId: number, gr: { reportTemplateId?: number | null }) =>
    assessmentId !== Number(actionAssessmentId) || matchesTemplate(gr),
  [actionAssessmentId, matchesTemplate]);

  const refreshReportData = useCallback(async (): Promise<Map<string, ReportData>> => {
    if (shownAssessments.length === 0 || selectedInstitute === "") { setReportDataMap(new Map()); return new Map(); }
    setReportDataLoading(true);
    try {
      const map = new Map<string, ReportData>();
      // Only the selected school's reports — the every-school list is super-admin only.
      const responses = await Promise.all(
        shownAssessments.map((a) =>
          getGeneratedReportsByInstitute(Number(selectedInstitute), a.id)
            .then((res) => ({ assessmentId: a.id, rows: res.data || [] }))
            // One assessment failing must not blank the whole table.
            .catch(() => ({ assessmentId: a.id, rows: [] as any[] })))
      );
      for (const { assessmentId, rows } of responses) {
        for (const gr of rows) {
          if (!keepReport(assessmentId, gr)) continue;
          const id = gr.userStudent?.userStudentId;
          if (!id) continue;
          const key = rowKey(assessmentId, id);
          const existing = map.get(key);
          // Prefer a generated row if a student somehow has multiple.
          if (!existing || gr.reportStatus === "generated") {
            map.set(key, {
              userStudent: { userStudentId: id },
              reportStatus: gr.reportStatus,
              reportUrl: gr.reportUrl,
              pdfUrl: gr.pdfUrl ?? null,
              pdfStatus: gr.pdfStatus ?? "notRequested",
              suppressionReason: gr.suppressionReason ?? null,
          });
          }
        }
      }
      setReportDataMap(map);
      return map;
    } catch {
      setReportDataMap(new Map());
      return new Map();
    } finally {
      setReportDataLoading(false);
    }
  }, [shownAssessments, keepReport, selectedInstitute]);

  const refreshVisibility = useCallback(async () => {
    if (shownAssessments.length === 0 || selectedInstitute === "") { setVisibilityMap(new Map()); return; }
    try {
      const map = new Map<string, { id: number; visible: boolean }>();
      const responses = await Promise.all(
        shownAssessments.map((a) =>
          getGeneratedReportsByInstitute(Number(selectedInstitute), a.id)
            .then((res) => ({ assessmentId: a.id, rows: res.data || [] }))
            .catch(() => ({ assessmentId: a.id, rows: [] as any[] })))
      );
      for (const { assessmentId, rows } of responses) {
        for (const gr of rows) {
          if (!keepReport(assessmentId, gr)) continue;
          map.set(rowKey(assessmentId, gr.userStudent.userStudentId), {
            id: gr.generatedReportId,
            visible: gr.visibleToStudent ?? false,
          });
        }
      }
      setVisibilityMap(map);
    } catch {
      setVisibilityMap(new Map());
    }
  }, [shownAssessments, keepReport, selectedInstitute]);

  // Load the templates mapped to this assessment; preselect the
  // default (else the first) so generation has a target.
  useEffect(() => {
    if (!selectedAssessmentObj) { setTemplates([]); setSelectedTemplateId(""); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await ReadAssessmentTemplates(selectedAssessmentObj.id);
        if (cancelled) return;
        const rows = res.data || [];
        setTemplates(rows);
        const def = rows.find((r) => r.isDefault) || rows[0];
        setSelectedTemplateId(def ? def.template.reportTemplateId : "");
      } catch {
        if (!cancelled) { setTemplates([]); setSelectedTemplateId(""); }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAssessmentObj]);

  // Ask the backend which Navigator engine this assessment runs, so the Navigator Pro
  // data export shows (and the 360 psychometric export is treated as wrong) even when
  // the mapped template's engineCode isn't set to navigator_pro.
  useEffect(() => {
    if (!selectedAssessmentObj) { setAssessmentIsPro(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await getAssessmentReportEngine(selectedAssessmentObj.id);
        if (!cancelled) setAssessmentIsPro(!!res.data?.navigatorPro);
      } catch {
        if (!cancelled) setAssessmentIsPro(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAssessmentObj]);

  useEffect(() => { refreshReportData(); refreshVisibility(); }, [refreshReportData, refreshVisibility]);

  // ═══════════════════════ FILTERING ═══════════════════════

  // Students enrolled in at least one of the shown assessments.
  const assessmentStudents = useMemo(() => {
    if (shownAssessments.length === 0) return [];
    const shownIds = new Set(shownAssessments.map((a) => a.id));
    return students.filter((s) =>
      (s.assignedAssessmentIds || []).some((id) => shownIds.has(id)));
  }, [students, shownAssessments]);

  // Apply the user's ABAC scopes (session/class/section) within the chosen
  // institute. Mirrors the backend predicate in AccessScopeJpqlBuilder: a
  // student is visible iff at least one of the user's scope rows matches every
  // non-null dimension. Super-admin / no scopes / institute-only-wildcard
  // scopes fall through and show all rows for the institute.
  const scopedStudents = useMemo(() => {
    if (isSuperAdmin) return assessmentStudents;
    if (!userScopes.length) return assessmentStudents;
    const instCode = selectedInstitute === "" ? null : Number(selectedInstitute);
    const matches = (scope: Scope, info: SectionInfo | undefined) => {
      if (scope.i != null && instCode != null && scope.i !== instCode) return false;
      if (scope.s != null && info?.sessionId !== scope.s) return false;
      if (scope.c != null && info?.classId !== scope.c) return false;
      if (scope.x != null && info?.sectionId !== scope.x) return false;
      return true;
    };
    // If every scope row constrains only the institute dimension, no further
    // filtering is needed inside this institute.
    const anyNarrower = userScopes.some((s) => s.s != null || s.c != null || s.x != null);
    if (!anyNarrower) return assessmentStudents;
    return assessmentStudents.filter((s) => {
      const info = s.schoolSectionId ? sectionLookup.get(s.schoolSectionId) : undefined;
      return userScopes.some((scope) => matches(scope, info));
    });
  }, [assessmentStudents, isSuperAdmin, userScopes, selectedInstitute, sectionLookup]);

  // Grade of a student: section hierarchy first, flat student_class as fallback
  // (many institutes were onboarded without section links — see 2026-07-07 diagnosis).
  const gradeOf = useCallback((s: StudentRow): string => {
    const info = s.schoolSectionId ? sectionLookup.get(s.schoolSectionId) : undefined;
    if (info?.className) return info.className;
    return s.studentClass != null ? String(s.studentClass) : "";
  }, [sectionLookup]);

  const uniqueGrades = useMemo(() => {
    const g = new Set<string>();
    for (const s of scopedStudents) {
      const grade = gradeOf(s);
      if (grade) g.add(grade);
    }
    return Array.from(g).sort();
  }, [scopedStudents, gradeOf]);

  const uniqueSections = useMemo(() => {
    const sec = new Set<string>();
    for (const s of scopedStudents) {
      const info = s.schoolSectionId ? sectionLookup.get(s.schoolSectionId) : undefined;
      if (info?.sectionName) sec.add(info.sectionName);
    }
    return Array.from(sec).sort();
  }, [scopedStudents, sectionLookup]);

  // Expand the scoped students into one row per (student, shown assessment)
  // they are enrolled in, grouped by student so a person's assessments sit
  // together on the page.
  const scopedRows = useMemo<StudentAssessmentRow[]>(() => {
    const rows: StudentAssessmentRow[] = [];
    for (const student of scopedStudents) {
      const assigned = new Set(student.assignedAssessmentIds || []);
      for (const assessment of shownAssessments) {
        if (!assigned.has(assessment.id)) continue;
        const detail = (student.assessments || []).find((a) => a.assessmentId === assessment.id);
        rows.push({
          key: rowKey(assessment.id, student.userStudentId),
          student,
          assessment,
          status: detail?.status || "notstarted",
          completedAt: (detail as any)?.completedAt,
        });
      }
    }
    return rows;
  }, [scopedStudents, shownAssessments]);

  const displayedRows = useMemo(() => {
    let result = scopedRows;
    if (nameQuery.trim()) {
      const q = nameQuery.toLowerCase();
      result = result.filter(({ student: s }) =>
        s.name.toLowerCase().includes(q) ||
        (s.username || "").toLowerCase().includes(q) ||
        (s.schoolRollNumber || "").toLowerCase().includes(q) ||
        (s.email || "").toLowerCase().includes(q) ||
        (s.studentDob || "").includes(q)
      );
    }
    if (usernameQuery.trim()) {
      const u = usernameQuery.trim().toLowerCase();
      result = result.filter(({ student: s }) => (s.username || "").toLowerCase().includes(u));
    }
    if (usernamePresence === "with") {
      result = result.filter(({ student: s }) => !!(s.username && s.username.trim()));
    } else if (usernamePresence === "without") {
      result = result.filter(({ student: s }) => !(s.username && s.username.trim()));
    }
    if (selectedGrade) result = result.filter(({ student: s }) => gradeOf(s) === selectedGrade);
    if (selectedSection) result = result.filter(({ student: s }) => sectionLookup.get(s.schoolSectionId!)?.sectionName === selectedSection);
    // Status and completion now read off the row's own assessment, so these
    // filters work across every assessment on the table at once.
    if (selectedStatus) result = result.filter((r) => r.status === selectedStatus);
    if (completedFrom || completedTo) {
      result = result.filter((r) => {
        const day = localDayKey(r.completedAt);
        // No completion date — never in range. Filtering by *when* someone finished
        // implies they finished, so unfinished students drop out here.
        if (!day) return false;
        if (completedFrom && day < completedFrom) return false;
        if (completedTo && day > completedTo) return false;
        return true;
      });
    }
    return result;
  }, [scopedRows, nameQuery, usernameQuery, usernamePresence, selectedGrade, selectedSection, selectedStatus, completedFrom, completedTo, sectionLookup, gradeOf]);

  /** Displayed rows belonging to the assessment the bulk actions act on. */
  const actionRows = useMemo(
    () => (actionAssessmentId === "" ? [] : displayedRows.filter((r) => r.assessment.id === Number(actionAssessmentId))),
    [displayedRows, actionAssessmentId]
  );

  /** Distinct students on the table — the roster count, as opposed to row count. */
  const displayedStudentCount = useMemo(
    () => new Set(displayedRows.map((r) => r.student.userStudentId)).size,
    [displayedRows]
  );

  const totalPages = Math.max(1, Math.ceil(displayedRows.length / pageSize));
  // Clamped rather than reset, so shrinking the result set past the current page
  // still renders rows instead of an empty table.
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedRows = useMemo(
    () => displayedRows.slice((safeCurrentPage - 1) * pageSize, safeCurrentPage * pageSize),
    [displayedRows, safeCurrentPage, pageSize]
  );

  useEffect(() => { setCurrentPage(1); }, [nameQuery, usernameQuery, usernamePresence, selectedGrade, selectedSection, selectedStatus, completedFrom, completedTo]);

  // Secret unlock: typing exactly "boom" in the username search toggles admin edit mode.
  useEffect(() => {
    if (usernameQuery === "boom") {
      setUsernameQuery("");
      setAdminEditMode((prev) => {
        const next = !prev;
        showSuccessToast(next ? "Admin edit mode enabled" : "Admin edit mode disabled");
        return next;
      });
    }
  }, [usernameQuery]);

  // ═══════════════════════ HELPERS ═══════════════════════

  /** Report row for one (assessment, student) pairing. */
  const reportOf = useCallback(
    (assessmentId: number, userStudentId: number) => reportDataMap.get(rowKey(assessmentId, userStudentId)),
    [reportDataMap]
  );
  /** Report row under the action assessment — what every bulk action works off. */
  const actionReportOf = useCallback(
    (userStudentId: number) =>
      actionAssessmentId === "" ? undefined : reportDataMap.get(rowKey(Number(actionAssessmentId), userStudentId)),
    [reportDataMap, actionAssessmentId]
  );
  const visibilityOf = useCallback(
    (assessmentId: number, userStudentId: number) => visibilityMap.get(rowKey(assessmentId, userStudentId)),
    [visibilityMap]
  );

  // Ticked, on-screen rows *of the action assessment* — ticking rows of another
  // shown assessment never feeds a bulk action, which is what the action picker
  // means. Nobody starts ticked, so an empty result means the user picked
  // nobody rather than a silent fall back to everyone.
  const getSelectedIds = () => actionRows
    .filter((r) => selectedRowKeys.has(r.key))
    .map((r) => r.student.userStudentId);

  /** Student behind a ticked id, looked up among the action assessment's rows. */
  const actionStudentOf = useCallback(
    (userStudentId: number) => actionRows.find((r) => r.student.userStudentId === userStudentId)?.student,
    [actionRows]
  );

  const visibleSelectedCount = useMemo(
    () => actionRows.filter((r) => selectedRowKeys.has(r.key)).length,
    [selectedRowKeys, actionRows]
  );

  /** Ticked rows that belong to some other assessment, so the UI can say so. */
  const otherAssessmentSelectedCount = useMemo(
    () => displayedRows.filter((r) => selectedRowKeys.has(r.key) && r.assessment.id !== Number(actionAssessmentId)).length,
    [selectedRowKeys, displayedRows, actionAssessmentId]
  );

  // Counted over the action assessment's rows: these numbers gate the bulk
  // Show/Hide/Send buttons, which only ever touch that assessment.
  const reportStats = useMemo(() => {
    let generated = 0, notGenerated = 0, completed = 0;
    for (const r of actionRows) {
      const rd = reportOf(r.assessment.id, r.student.userStudentId);
      if (rd && rd.reportStatus === "generated") generated++;
      else notGenerated++;
      if (r.status === "completed") completed++;
    }
    return { generated, notGenerated, completed };
  }, [actionRows, reportOf]);

  const downloadBlob = (data: any, filename: string) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const a = document.createElement("a"); a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  };

  const selectedInstituteName = institutes.find((i) => i.instituteCode === selectedInstitute)?.instituteName || "";
  const selectedAssessmentName = selectedAssessmentObj?.assessmentName || "";
  const ready = selectedInstitute !== "" && shownAssessments.length > 0;
  const accentColor = isBet ? "#4361ee" : "#0d9488";

  // ═══════════════════════ GENERATE (opens modal) ═══════════════════════

  const openGenerateModal = () => {
    if (!selectedAssessmentObj) return;
    const ids = getSelectedIds();
    if (ids.length === 0) { showErrorToast("Select at least one student."); return; }
    if (templates.length === 0) {
      showErrorToast("No report template is mapped to this assessment. Map one in Report Templates or the assessment editor first.");
      return;
    }
    const list: ModalStudent[] = ids.map((id) => {
      const s = students.find((st) => st.userStudentId === id);
      return { userStudentId: id, name: s?.name || `Student ${id}`, username: s?.username };
    });
    setModalStudents(list);
    setGenerateModalOpen(true);
  };

  // Same student-list construction, but opens the async (Kafka → report-worker) modal.
  const openQueueModal = () => {
    if (!selectedAssessmentObj) return;
    const ids = getSelectedIds();
    if (ids.length === 0) { showErrorToast("Select at least one student."); return; }
    if (templates.length === 0) {
      showErrorToast("No report template is mapped to this assessment. Map one in Report Templates or the assessment editor first.");
      return;
    }
    const list: ModalStudent[] = ids.map((id) => {
      const s = students.find((st) => st.userStudentId === id);
      return { userStudentId: id, name: s?.name || `Student ${id}`, username: s?.username };
    });
    setModalStudents(list);
    setQueueModalOpen(true);
  };

  // Refresh page maps after generation inside the modal (component refresh, no reload).
  const onModalGenerated = useCallback(() => {
    refreshReportData();
    refreshVisibility();
  }, [refreshReportData, refreshVisibility]);

  // ═══════════════════════ VISIBILITY ═══════════════════════

  const handleToggleVisibility = async (assessmentId: number, studentId: number) => {
    const key = rowKey(assessmentId, studentId);
    const entry = visibilityMap.get(key);
    if (!entry) return;
    const newVisible = !entry.visible;
    try {
      await toggleReportVisibility([entry.id], newVisible);
      setVisibilityMap((prev) => {
        const next = new Map(prev);
        next.set(key, { ...entry, visible: newVisible });
        return next;
      });
    } catch { showErrorToast("Failed to update visibility"); }
  };

  const handleBulkVisibility = async (visible: boolean) => {
    const ids = getSelectedIds()
      .map((sid) => (actionAssessmentId === "" ? undefined : visibilityMap.get(rowKey(Number(actionAssessmentId), sid))))
      .filter((e): e is { id: number; visible: boolean } => !!e && e.visible !== visible)
      .map((e) => e.id);
    if (ids.length === 0) {
      showSuccessToast(`All selected reports are already ${visible ? "released" : "hidden"}`);
      return;
    }
    setTogglingVisibility(true);
    try {
      await toggleReportVisibility(ids, visible);
      await refreshVisibility();
      showSuccessToast(`${ids.length} report(s) ${visible ? "released" : "hidden"}`);
    } catch { showErrorToast("Failed to update visibility"); }
    finally { setTogglingVisibility(false); }
  };

  // ═══════════════════════ DOWNLOAD ZIP → DO Spaces ═══════════════════════

  // Step 1: Click button → open name prompt
  const handleDownloadZipClick = () => {
    if (!selectedAssessmentObj) return;
    let ids = getSelectedIds().filter((id) => {
      const rd = actionReportOf(id);
      return rd && rd.pdfStatus === "ready" && rd.pdfUrl;
    });
    if (ids.length === 0) { showErrorToast("No rendered PDFs to download yet."); return; }
    pendingZipIds.current = ids;
    pendingZipKind.current = "navigator";
    setZipNameInput(`${reportType}_reports_${selectedAssessmentObj.assessmentName.replace(/[^a-zA-Z0-9]/g, "_")}`);
    setZipNamePromptOpen(true);
  };

  // Step 2: Confirm name → start async background job.
  // PDFs are pre-rendered server-side and stored on Spaces, so this just bundles
  // the existing pdfUrls (fetch + zip) and uploads the ZIP — no client conversion.
  const startZipJob = (zipName: string) => {
    if (!selectedAssessmentObj) return;
    const ids = [...pendingZipIds.current];
    const jobId = `zip-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const fileName = zipName.endsWith(".zip") ? zipName : zipName + ".zip";

    const newJob: ZipJob = {
      id: jobId,
      name: fileName,
      status: "zipping",
      phase: "Bundling PDFs...",
      progress: 0,
      createdAt: Date.now(),
    };
    setZipJobs((prev) => [newJob, ...prev]);

    const updateJob = (patch: Partial<ZipJob>) => {
      setZipJobs((prev) => prev.map((j) => j.id === jobId ? { ...j, ...patch } : j));
    };

    // Fire and forget
    (async () => {
      try {
        // Build the bundle list from the stored PDF URLs.
        const items = ids
          .map((id) => {
            const s = students.find((st) => st.userStudentId === id);
            const safe = (s?.name || `student_${id}`).replace(/[^a-zA-Z0-9 ]/g, "").replace(/\s+/g, "_");
            const rd = actionReportOf(id);
            return { fileName: `${safe}_report`, pdfUrl: rd?.pdfUrl ?? null };
          })
          .filter((x) => !!x.pdfUrl);

        if (items.length === 0) {
          updateJob({ status: "error", phase: undefined, error: "No rendered PDFs to bundle (still generating?)" });
          showErrorToast("No rendered PDFs to bundle yet.");
          return;
        }

        updateJob({ phase: `Bundling ${items.length} PDF(s)...`, progress: 40 });
        const { blob: zipBlob, added, skipped } = await zipStoredPdfs(items);
        if (added === 0) {
          updateJob({ status: "error", phase: undefined, error: "No PDFs could be downloaded" });
          showErrorToast("No PDFs could be downloaded.");
          return;
        }

        // Upload the ZIP to DO Spaces (unchanged).
        updateJob({ status: "uploading", phase: "Uploading to cloud...", progress: 80 });
        const uploadRes = await uploadReportZip(zipBlob, fileName);
        const { url, downloadUrl } = uploadRes.data;

        updateJob({ status: "done", phase: undefined, progress: 100, url, downloadUrl });
        showSuccessToast(
          skipped.length
            ? `"${fileName}" ready — ${added} PDF(s), ${skipped.length} skipped (not ready)`
            : `"${fileName}" ready for download!`
        );
      } catch (err: any) {
        updateJob({
          status: "error",
          phase: undefined,
          error: err?.response?.data?.error || err.message || "Unknown error",
        });
        showErrorToast("ZIP generation failed.");
      }
    })();
  };

  // Delete a completed ZIP from DO Spaces (or cancel/clear a server auto ZIP).
  const handleDeleteZipJob = async (job: ZipJob) => {
    if (job.serverJobId) {
      const serverJobId = job.serverJobId;
      const wasActive = job.status === "zipping" || job.status === "uploading";
      setDeletingJobs((prev) => new Set(prev).add(job.id));
      try {
        await deleteAutoZipJob(serverJobId);
        setAutoZipJobs((prev) => prev.filter((j) => j.id !== serverJobId));
        showSuccessToast(wasActive ? `"${job.name}" cancelled.` : `"${job.name}" deleted.`);
      } catch (err: any) {
        showErrorToast("Delete failed: " + (err?.response?.data?.error || err.message));
      } finally {
        setDeletingJobs((prev) => { const n = new Set(prev); n.delete(job.id); return n; });
      }
      return;
    }
    if (!job.url) return;
    setDeletingJobs((prev) => new Set(prev).add(job.id));
    try {
      await deleteReportZip(job.url);
      setZipJobs((prev) => prev.filter((j) => j.id !== job.id));
      showSuccessToast(`"${job.name}" deleted.`);
    } catch (err: any) {
      showErrorToast("Delete failed: " + (err?.response?.data?.error || err.message));
    } finally {
      setDeletingJobs((prev) => { const n = new Set(prev); n.delete(job.id); return n; });
    }
  };

  // ═══════════════════════ AUTO ZIP (server-side, whole school) ═══════════════════════

  // Pick up the user's server jobs on load, so a refresh doesn't lose them.
  useEffect(() => {
    listAutoZipJobs().then((res) => setAutoZipJobs(res.data || [])).catch(() => { /* list stays empty */ });
  }, []);

  // Poll while any job is still running, and say so when one finishes.
  const autoZipJobsRef = useRef<AutoZipJob[]>(autoZipJobs);
  autoZipJobsRef.current = autoZipJobs;
  const hasActiveAutoZip = autoZipJobs.some(isAutoZipActive);
  useEffect(() => {
    if (!hasActiveAutoZip) return;
    const timer = window.setInterval(async () => {
      try {
        const res = await listAutoZipJobs();
        const next = res.data || [];
        for (const j of next) {
          const before = autoZipJobsRef.current.find((p) => p.id === j.id);
          if (!before || !isAutoZipActive(before)) continue;
          if (j.status === "done") showSuccessToast(`"${j.name}" is ready — ${j.added} PDF(s). Open Downloads.`);
          else if (j.status === "error") showErrorToast(`"${j.name}" failed: ${j.error || "unknown error"}`);
        }
        setAutoZipJobs(next);
      } catch { /* try again next tick */ }
    }, 3000);
    return () => window.clearInterval(timer);
  }, [hasActiveAutoZip]);

  const onAutoZipStarted = (job: AutoZipJob) => {
    setAutoZipJobs((prev) => [job, ...prev.filter((j) => j.id !== job.id)]);
    showSuccessToast(`Building "${job.name}" — ${job.total} PDF(s). Track it in Downloads.`);
  };

  /** Browser-built and server-built ZIPs together, newest first. */
  const allZipJobs = useMemo(
    () => [...autoZipJobs.map(autoZipToDownload), ...zipJobs].sort((a, b) => b.createdAt - a.createdAt),
    [autoZipJobs, zipJobs]
  );
  const activeZipJobs = allZipJobs.filter((j) => j.status === "zipping" || j.status === "uploading");
  const canAutoZip = selectedInstitute !== "" && assessments.length > 0;

  // ═══════════════════════ MIRA DESAI ACTIONS ═══════════════════════

  const handleGenerateDataExcel = async () => {
    if (!selectedAssessmentObj) return;
    const ids = getSelectedIds();
    if (ids.length === 0) { showErrorToast("No students."); return; }
    setExportingDataExcel(true);
    try {
      const res = await exportAssessmentDataExcel(selectedAssessmentObj.id, ids);
      downloadBlob(res.data, `assessment_data_${selectedAssessmentObj.id}.xlsx`);
      showSuccessToast(`Generated data for ${ids.length} student(s).`);
    } catch (err: any) { showErrorToast("Generation failed: " + (err?.response?.data?.error || err.message)); }
    finally { setExportingDataExcel(false); }
  };

  const handleExportBetCoreData = async () => {
    if (!selectedAssessmentObj) return;
    const selectedVisible = getSelectedIds();
    const ids = selectedVisible.length > 0 ? selectedVisible : undefined;
    setExportingMQT(true);
    try {
      const res = await exportMqtScoresExcel(selectedAssessmentObj.id, ids);
      downloadBlob(res.data, `bet_core_data_${selectedAssessmentObj.id}.xlsx`);
    } catch (err: any) { showErrorToast("Export failed: " + (err?.response?.data?.error || err.message)); }
    finally { setExportingMQT(false); }
  };

  // Navigator360 school dashboard. Every active filter is already baked into
  // actionRows, so sending those ids is what keeps the export in step
  // with the table — unlike the other exports, an empty selection falls back to
  // the filtered list rather than to the whole assessment.
  const handleExportDashboardSheet = async () => {
    if (!selectedAssessmentObj) return;
    const ticked = getSelectedIds();
    const ids = ticked.length > 0 ? ticked : actionRows.map((r) => r.student.userStudentId);
    if (ids.length === 0) { showErrorToast("No students match the current filters."); return; }

    // The grade filter doubles as sheet 2's CLASS FILTER when it is a plain
    // class number; named grades ("Class 10", "X") have no numeric equivalent,
    // and the id list already restricts the cohort either way.
    const classFilter = /^\d+$/.test(selectedGrade.trim()) ? selectedGrade.trim() : "All";

    setExportingDashboard(true);
    try {
      const res = await exportSchoolDashboardExcel(selectedAssessmentObj.id, ids, classFilter);
      downloadBlob(res.data, `school_dashboard_${selectedAssessmentObj.id}.xlsx`);
      showSuccessToast(`Dashboard built for ${ids.length} student(s).`);
      setMiraDesaiOpen(false);
    } catch (err: any) {
      // The endpoint answers with JSON on failure, so a blob response has to be
      // read back as text before the message is visible.
      let message = err?.message || "Unknown error";
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        try { message = JSON.parse(await blob.text()).error || message; } catch { /* keep message */ }
      } else if (err?.response?.data?.error) {
        message = err.response.data.error;
      }
      showErrorToast("Dashboard export failed: " + message);
    } finally { setExportingDashboard(false); }
  };

  // The full psychometric-properties study workbook (reliability, EFA, item
  // analysis, IRT, norms, CFA screening). Statistics need the whole cohort to
  // be meaningful, so like the dashboard an empty selection falls back to the
  // filtered list rather than aborting.
  const handleExportPsychometricProperties = async () => {
    if (!selectedAssessmentObj) return;
    const ticked = getSelectedIds();
    const ids = ticked.length > 0 ? ticked : actionRows.map((r) => r.student.userStudentId);
    if (ids.length === 0) { showErrorToast("No students match the current filters."); return; }

    setExportingPsychometric(true);
    try {
      const res = await exportPsychometricProperties(selectedAssessmentObj.id, ids);
      downloadBlob(res.data, `psychometric_properties_navigator360_${selectedAssessmentObj.id}.xlsx`);
      showSuccessToast(`Psychometric properties computed for ${ids.length} student(s).`);
      setMiraDesaiOpen(false);
    } catch (err: any) {
      let message = err?.message || "Unknown error";
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        try { message = JSON.parse(await blob.text()).error || message; } catch { /* keep message */ }
      } else if (err?.response?.data?.error) {
        message = err.response.data.error;
      }
      showErrorToast("Psychometric export failed: " + message);
    } finally { setExportingPsychometric(false); }
  };

  // Navigator Pro raw data: the v3 engine's full per-student computation. Like the
  // psychometric export, an empty selection falls back to the filtered list.
  const isNavigatorPro = assessmentIsPro || templates.some((m) => m.template.engineCode === "navigator_pro");
  const handleExportNavigatorProRaw = async () => {
    if (!selectedAssessmentObj) return;
    const ticked = getSelectedIds();
    const ids = ticked.length > 0 ? ticked : actionRows.map((r) => r.student.userStudentId);
    if (ids.length === 0) { showErrorToast("No students match the current filters."); return; }

    setExportingNavigatorPro(true);
    try {
      const res = await exportNavigatorProRaw(selectedAssessmentObj.id, ids);
      downloadBlob(res.data, `navigator_pro_raw_data_${selectedAssessmentObj.id}.xlsx`);
      showSuccessToast(`Navigator Pro raw data computed for ${ids.length} student(s).`);
      setMiraDesaiOpen(false);
    } catch (err: any) {
      let message = err?.message || "Unknown error";
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        try { message = JSON.parse(await blob.text()).error || message; } catch { /* keep message */ }
      } else if (err?.response?.data?.error) {
        message = err.response.data.error;
      }
      showErrorToast("Navigator Pro export failed: " + message);
    } finally { setExportingNavigatorPro(false); }
  };

  // ═══════════════════════ SEND ACTIONS ═══════════════════════

  const handleSendEmail = (row: StudentAssessmentRow) => {
    const { student } = row;
    const rd = reportOf(row.assessment.id, student.userStudentId);
    if (!student.email || !rd?.reportUrl) return;
    setComposeStudentIds([student.userStudentId]);
    setComposeRecipients([{ email: student.email, name: student.name }]);
    setComposeSubject(`Your ${row.assessment.assessmentName} Report - Career-9`);
    setComposeBody(buildEmailTemplate(student.name, rd.reportUrl, row.assessment.assessmentName));
    setComposeOpen(true);
  };

  const handleSendWhatsApp = async (row: StudentAssessmentRow) => {
    const { student } = row;
    const rd = reportOf(row.assessment.id, student.userStudentId);
    if (!student.phoneNumber || !rd?.reportUrl) return;
    setSendingWhatsApp((prev) => new Set(prev).add(student.userStudentId));
    try {
      await SendWhatsApp(student.phoneNumber, "report_notification", [
        student.name, row.assessment.assessmentName, rd.reportUrl,
      ]);
      showSuccessToast(`WhatsApp sent to ${student.name}`);
    } catch (err: any) {
      showErrorToast(`WhatsApp failed for ${student.name}: ${err?.response?.data?.error || err.message}`);
    } finally {
      setSendingWhatsApp((prev) => { const n = new Set(prev); n.delete(student.userStudentId); return n; });
    }
  };

  const handleBulkEmail = () => {
    const selected = getSelectedIds()
      .map((id) => {
        const s = actionStudentOf(id);
        const rd = actionReportOf(id);
        return s && s.email && rd?.reportUrl ? { ...s, reportUrl: rd.reportUrl } : null;
      })
      .filter((s): s is StudentRow & { reportUrl: string } => !!s);
    if (selected.length === 0) { showErrorToast("No students with email + generated reports."); return; }
    setComposeStudentIds(selected.map((s) => s.userStudentId));
    setComposeRecipients(selected.map((s) => ({ email: s.email!, name: s.name })));
    setComposeSubject(`Your ${selectedAssessmentName} Report - Career-9`);
    setComposeBody(buildEmailTemplate("{{student_name}}", "{{report_link}}", selectedAssessmentName));
    setComposeOpen(true);
  };

  const handleComposeSend = async (
    emails: string[], subject: string, body: string, overrideAccountId: number | null
  ) => {
    setComposeSending(true);
    try {
      if (emails.length === 1 && composeStudentIds.length === 1) {
        await SendReportEmail(emails, subject, body, undefined, overrideAccountId);
        showSuccessToast(`Email sent to ${emails[0]}`);
      } else {
        // Bulk: personalize for each student
        const studentsToSend = composeStudentIds
          .map((id) => {
            const s = actionStudentOf(id);
            const rd = actionReportOf(id);
            return s && s.email ? { name: s.name, email: s.email, reportUrl: rd?.reportUrl || "" } : null;
          })
          .filter((s): s is { name: string; email: string; reportUrl: string } => !!s);

        let sentCount = 0;
        for (const student of studentsToSend) {
          const personalizedBody = body
            .replace(/\{\{student_name\}\}/g, student.name)
            .replace(/\{\{report_link\}\}/g, student.reportUrl);
          try {
            await SendReportEmail([student.email], subject, personalizedBody, undefined, overrideAccountId);
            sentCount++;
          } catch { /* continue */ }
        }
        showSuccessToast(`${sentCount}/${studentsToSend.length} email(s) sent.`);
      }
      setComposeOpen(false);
    } catch (err: any) {
      showErrorToast(`Send failed: ${err?.response?.data || err.message}`);
    } finally {
      setComposeSending(false);
    }
  };

  const handleBulkWhatsApp = async () => {
    const selected = getSelectedIds()
      .map((id) => {
        const s = actionStudentOf(id);
        const rd = actionReportOf(id);
        return s && s.phoneNumber && rd?.reportUrl
          ? { phoneNumber: s.phoneNumber, name: s.name, reportUrl: rd.reportUrl }
          : null;
      })
      .filter((s): s is { phoneNumber: string; name: string; reportUrl: string } => !!s);
    if (selected.length === 0) { showErrorToast("No students with phone + generated reports."); return; }
    try {
      const recipients = selected.map((s) => ({
        phoneNumber: s.phoneNumber,
        templateParams: [s.name, selectedAssessmentName, s.reportUrl],
      }));
      const res = await SendWhatsAppBulk("report_notification", recipients);
      const d = res.data;
      showSuccessToast(`WhatsApp: ${d.successCount} sent, ${d.failCount} failed of ${d.totalRecipients}`);
    } catch (err: any) {
      showErrorToast(`Bulk WhatsApp failed: ${err?.response?.data?.error || err.message}`);
    }
  };

  // ═══════════════════════ STYLES ═══════════════════════

  // Tighter than a default table: the Assessment column is the 13th, and the
  // whole row has to fit the page width without a horizontal scrollbar.
  const thStyle: React.CSSProperties = { padding: "9px 8px", fontWeight: 600, color: "#1a1a2e", borderBottom: "2px solid #e0e0e0", fontSize: "0.78rem", textAlign: "left", overflow: "hidden", textOverflow: "ellipsis" };
  const tdStyle: React.CSSProperties = { padding: "9px 8px", borderBottom: "1px solid #f0f0f0", fontSize: "0.8rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
  /** Wrapping cell for the two long text columns, so nothing is cut off mid-name. */
  const tdWrapStyle: React.CSSProperties = { ...tdStyle, whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.25 };
  /**
   * The action bar must not reflow when something is clicked. Every button gets
   * a fixed width, so a changing label — "Generate (0)" to "Generate (12)",
   * "Release" to "..." — cannot resize it and shove its neighbours along; the
   * text is centred and clipped inside that fixed box instead.
   */
  const actionBtn = (width: number): React.CSSProperties => ({
    width, flex: `0 0 ${width}px`, boxSizing: "border-box",
    padding: "8px 6px", borderRadius: 8, fontWeight: 600, fontSize: "0.85rem",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "center",
  });

  /** Percentage widths, in column order; the last entry is the admin-only column. */
  const colWidths = useMemo(() => {
    const base = ["3%", "3.5%", "12%", "8.5%", "15%", "8%", "8%", "5.5%", "6.5%", "8%", "5%", "9%", "8%"];
    return adminEditMode ? [...base, "6%"] : base;
  }, [adminEditMode]);
  const statusBadge = (bg: string, color: string, text: string) => (
    <span style={{ background: bg, color, padding: "3px 10px", borderRadius: 6, fontWeight: 600, fontSize: "0.75rem" }}>{text}</span>
  );

  const countLabel = ` (${visibleSelectedCount})`;

  // ═══════════════════════ RENDER ═══════════════════════

  return (
    <div className="ph-page">
      <style>{`
        @media (max-width: 991.98px) {
          .rh-table-wrap { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }
          .rh-table { min-width: 1200px; }
        }
        @media (max-width: 767.98px) {
          .rh-pickers { grid-template-columns: 1fr !important; padding: 16px !important; }
          .rh-main { padding: 16px !important; }
        }
      `}</style>
      <PageHeader
        icon={<i className="bi bi-grid-3x3-gap" />}
        title="Reports Hub"
        subtitle={
          ready ? (
            <>
              <strong>{displayedStudentCount}</strong> students · <strong>{displayedRows.length}</strong> rows
              {" · "}
              {shownAssessments.length === 1
                ? shownAssessments[0].assessmentName
                : `${shownAssessments.length} assessments`}
              {" · "}
              <strong>{reportStats.generated}</strong> reports generated in {selectedAssessmentName || "—"}
            </>
          ) : (
            <>Generate, manage, export, and send reports from one place</>
          )
        }
        actions={[
          {
            label: `Generate${countLabel}`,
            iconClass: "bi-play-circle",
            onClick: openGenerateModal,
            variant: "primary",
            // Pinned: the label carries a live selection count.
            minWidth: 148,
            disabled: !ready || actionRows.length === 0,
          },
          {
            label: `Queue${countLabel}`,
            iconClass: "bi-stack",
            onClick: openQueueModal,
            variant: "ghost",
            minWidth: 128,
            disabled: !ready || actionRows.length === 0,
          },
          {
            label: "Auto ZIP",
            iconClass: "bi-file-earmark-zip-fill",
            onClick: () => setAutoZipOpen(true),
            variant: "ghost",
            // Needs only a school — assessments are picked inside the modal.
            disabled: !canAutoZip,
          },
          {
            label: "Download ZIP",
            iconClass: "bi-file-earmark-zip",
            onClick: handleDownloadZipClick,
            variant: "ghost",
            disabled: !ready || reportStats.generated === 0,
          },
          {
            label: "Bulk Send",
            iconClass: "bi-send",
            onClick: () => setBulkSendOpen(true),
            variant: "ghost",
            disabled: !ready || reportStats.generated === 0,
          },
        ]}
      />

      {/* Selection Row */}
      <div className="rh-pickers" style={{
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24,
        background: "#fff", padding: 20, borderRadius: 12,
        border: "1px solid #e5e7eb", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}>
        <div>
          <label style={{ fontWeight: 600, fontSize: "0.85rem", color: "#374151", marginBottom: 6, display: "block" }}>
            School / Institute
          </label>
          {institutesLoading ? (
            <div style={{ color: "#9ca3af", padding: "8px 0" }}>Loading...</div>
          ) : (
            <SearchableSelect
              options={institutes.map((inst) => ({
                value: String(inst.instituteCode),
                label: String(inst.instituteName ?? ""),
              }))}
              value={selectedInstitute === "" ? "" : String(selectedInstitute)}
              onChange={(v) => setSelectedInstitute(v === "" ? "" : Number(v))}
              placeholder="-- Select a school --"
            />
          )}
        </div>
        <div>
          <label style={{ fontWeight: 600, fontSize: "0.85rem", color: "#374151", marginBottom: 6, display: "block" }}>
            Assessments
            {shownAssessments.length > 0 && (
              <span style={{ fontWeight: 500, color: "#6b7280" }}> · {shownAssessments.length} selected</span>
            )}
          </label>
          {assessmentsLoading ? (
            <div style={{ color: "#9ca3af", padding: "8px 0" }}>Loading...</div>
          ) : selectedInstitute !== "" && assessments.length === 0 ? (
            <div style={{
              padding: "9px 12px", borderRadius: 8, border: "1px dashed #fcd34d",
              background: "#fffbeb", color: "#92400e", fontSize: "0.8rem",
            }}>
              No assessments are mapped to this school yet.
            </div>
          ) : (
            <SearchableMultiSelect
              options={assessments.map((a) => ({
                value: String(a.id),
                label: `${a.assessmentName} [${getReportType(a).toUpperCase()}]`,
              }))}
              value={selectedAssessmentIds.map(String)}
              onChange={(vals) => setSelectedAssessmentIds(vals.map(Number))}
              disabled={selectedInstitute === ""}
              placeholder="-- Select one or more assessments --"
            />
          )}
        </div>
      </div>

      {/* Empty state */}
      {!ready && (
        <div style={{
          padding: 48, textAlign: "center", color: "#9ca3af",
          border: "2px dashed #e5e7eb", borderRadius: 12, background: "#fff",
        }}>
          <div style={{ fontSize: "2rem", marginBottom: 8, opacity: 0.4 }}><i className="bi bi-bar-chart" /></div>
          <div>Select a school and at least one assessment to get started</div>
        </div>
      )}

      {/* Main content */}
      {ready && (
        <div className="rh-main" style={{
          background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)", padding: 24,
        }}>
          {/* Summary bar */}
          <div style={{
            padding: "12px 20px", background: accentColor + "10", borderRadius: 10,
            display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap",
          }}>
            <span style={{ fontWeight: 700, color: accentColor }}>{selectedInstituteName}</span>
            <span style={{ color: "#cbd5e1" }}>/</span>
            {/* Actions are per assessment, so the bar names the one they act on
                and lets it be switched without changing what the table shows. */}
            <span style={{ fontSize: "0.8rem", color: "#475569", fontWeight: 600 }}>Actions on:</span>
            {shownAssessments.length === 1 ? (
              <span style={{ fontWeight: 600, color: "#1e293b" }}>{selectedAssessmentName}</span>
            ) : (
              <select
                className="form-select form-select-sm"
                value={actionAssessmentId === "" ? "" : String(actionAssessmentId)}
                onChange={(e) => setActionAssessmentId(e.target.value === "" ? "" : Number(e.target.value))}
                style={{ width: "auto", minWidth: 220, fontSize: "0.82rem", fontWeight: 600 }}
                title="Generation, sending and the Excel exports run on this assessment; ticked rows of the others are ignored"
              >
                {shownAssessments.map((a) => (
                  <option key={a.id} value={a.id}>{a.assessmentName}</option>
                ))}
              </select>
            )}
            <span style={{
              background: accentColor + "18", color: accentColor,
              padding: "2px 10px", borderRadius: 6, fontWeight: 700, fontSize: "0.75rem",
            }}>{reportType.toUpperCase()}</span>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <span style={{ background: accentColor, color: "#fff", padding: "4px 12px", borderRadius: 16, fontSize: "0.8rem", fontWeight: 600 }}>
                {assessmentStudents.length} students
              </span>
              <span style={{ background: "#4361ee", color: "#fff", padding: "4px 12px", borderRadius: 16, fontSize: "0.8rem", fontWeight: 600 }}>
                {reportStats.generated} reports
              </span>
            </div>
          </div>

          {studentsLoading || reportDataLoading ? (
            <div style={{ color: "#9ca3af", padding: 24 }}>Loading...</div>
          ) : (
            <>
              {/* Search + Filters */}
              <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
                <div style={{ flex: "1 1 240px", minWidth: 200 }}>
                  <label style={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 500 }}>Search</label>
                  <input className="form-control form-control-sm form-control-solid"
                    placeholder="Name, roll no, email, DOB..."
                    value={nameQuery} onChange={(e) => setNameQuery(e.target.value)} />
                </div>
                <div style={{ minWidth: 140 }}>
                  <label style={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 500 }}>Grade / Class</label>
                  <select className="form-select form-select-sm form-select-solid" value={selectedGrade}
                    onChange={(e) => setSelectedGrade(e.target.value)}>
                    <option value="">All</option>
                    {uniqueGrades.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
                <div style={{ minWidth: 140 }}>
                  <label style={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 500 }}>Section</label>
                  <select className="form-select form-select-sm form-select-solid" value={selectedSection}
                    onChange={(e) => setSelectedSection(e.target.value)}>
                    <option value="">All</option>
                    {uniqueSections.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div style={{ minWidth: 160 }}>
                  <label style={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 500 }}>Status</label>
                  <select
                    className="form-select form-select-sm form-select-solid"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value as "" | "completed" | "ongoing" | "notstarted")}
                    disabled={!selectedAssessmentObj}
                    title={!selectedAssessmentObj ? "Select an assessment first" : ""}
                  >
                    <option value="">All</option>
                    <option value="completed">Completed</option>
                    <option value="ongoing">Ongoing</option>
                    <option value="notstarted">Not Started</option>
                  </select>
                </div>
                <div style={{ minWidth: 150 }}>
                  <label style={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 500 }}>Completed From</label>
                  <input
                    type="date"
                    className="form-control form-control-sm form-control-solid"
                    value={completedFrom}
                    max={completedTo || undefined}
                    onChange={(e) => setCompletedFrom(e.target.value)}
                    disabled={!selectedAssessmentObj}
                    title={!selectedAssessmentObj ? "Select an assessment first" : ""}
                  />
                </div>
                <div style={{ minWidth: 150 }}>
                  <label style={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 500 }}>Completed To</label>
                  <input
                    type="date"
                    className="form-control form-control-sm form-control-solid"
                    value={completedTo}
                    min={completedFrom || undefined}
                    onChange={(e) => setCompletedTo(e.target.value)}
                    disabled={!selectedAssessmentObj}
                    title={!selectedAssessmentObj ? "Select an assessment first" : ""}
                  />
                </div>
                {(completedFrom || completedTo) && (
                  <div>
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => { setCompletedFrom(""); setCompletedTo(""); }}
                    >
                      Clear dates
                    </button>
                  </div>
                )}
                <div style={{ minWidth: 180 }}>
                  <label style={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 500 }}>Username</label>
                  <input className="form-control form-control-sm form-control-solid"
                    placeholder="Filter by username..."
                    value={usernameQuery} onChange={(e) => setUsernameQuery(e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 500, display: "block" }}>
                    Has Username
                  </label>
                  <div className="btn-group btn-group-sm" role="group" aria-label="Username presence filter">
                    {(["all", "with", "without"] as const).map((key) => {
                      const label = key === "all" ? "All" : key === "with" ? "With" : "Without";
                      const active = usernamePresence === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`btn btn-sm ${active ? "btn-primary" : "btn-light"}`}
                          onClick={() => setUsernamePresence(key)}
                          style={{ fontWeight: active ? 600 : 500 }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Action bar */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                marginBottom: 12, flexWrap: "wrap", gap: 8,
              }}>
                {/* Fixed flex basis + clipping: the selection caption grows and
                    shrinks as rows are ticked, and must not move the buttons. */}
                <span style={{
                  fontSize: "0.85rem", color: "#6b7280",
                  flex: "1 1 240px", minWidth: 0,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {displayedRows.length} row(s) · {displayedStudentCount} student(s)
                  {visibleSelectedCount > 0 && (
                    <span style={{ fontWeight: 600, color: accentColor, marginLeft: 8 }}>
                      ({visibleSelectedCount} selected in {selectedAssessmentName})
                    </span>
                  )}
                  {otherAssessmentSelectedCount > 0 && (
                    <span
                      style={{ color: "#b45309", marginLeft: 8 }}
                      title="Bulk actions only use rows of the assessment named in 'Actions on'"
                    >
                      + {otherAssessmentSelectedCount} ticked in other assessments (not used)
                    </span>
                  )}
                  {adminEditMode && (
                    <span style={{
                      marginLeft: 12, padding: "2px 10px", borderRadius: 999,
                      background: "#fef3c7", color: "#92400e", fontWeight: 700,
                      fontSize: "0.7rem", border: "1px solid #f59e0b",
                    }}>
                      ADMIN EDIT MODE
                    </span>
                  )}
                </span>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flexShrink: 0, justifyContent: "flex-end" }}>
                  {/* Mira Desai */}
                  <button className="btn btn-sm" onClick={() => setMiraDesaiOpen(true)}
                    style={{
                      ...actionBtn(118),
                      background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      border: "none", color: "white",
                      boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
                    }}>
                    Mira Desai
                  </button>

                  {/* Report template picker (default preselected) */}
                  <select
                    className="form-select form-select-sm"
                    style={{ ...actionBtn(230), padding: "8px 6px", fontWeight: 500, textAlign: "left" }}
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value === "" ? "" : Number(e.target.value))}
                    disabled={templates.length === 0}
                    title={templates.length === 0
                      ? "No template mapped to this assessment"
                      // The box is a fixed width, so a long name is clipped — keep it readable here.
                      : `Template to generate: ${templates.find((m) => m.template.reportTemplateId === selectedTemplateId)?.template.displayName ?? ""}`}
                  >
                    {templates.length === 0 ? (
                      <option value="">No template mapped</option>
                    ) : (
                      templates.map((m) => (
                        <option key={m.template.reportTemplateId} value={m.template.reportTemplateId}>
                          {m.template.displayName}{m.isDefault ? " (default)" : ""}
                        </option>
                      ))
                    )}
                  </select>

                  {/* Generate All → opens the generation modal */}
                  <button className="btn btn-sm" disabled={actionRows.length === 0}
                    onClick={openGenerateModal}
                    style={{
                      ...actionBtn(122),
                      background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}cc 100%)`,
                      border: "none", color: "white",
                    }}>
                    {`Generate${countLabel}`}
                  </button>

                  {/* Generate via Kafka queue → report-worker */}
                  <button className="btn btn-sm btn-light" disabled={actionRows.length === 0}
                    onClick={openQueueModal}
                    style={actionBtn(104)}>
                    {`Queue${countLabel}`}
                  </button>

                  {/* Auto ZIP — whole school, by class & section, built on the server */}
                  <button className="btn btn-sm" disabled={!canAutoZip}
                    onClick={() => setAutoZipOpen(true)}
                    title="Zip every rendered report at this school, filed by class and section"
                    style={{
                      ...actionBtn(104),
                      background: "#fff", color: "#5b21b6",
                      border: "1px solid #7c3aed",
                    }}>
                    Auto ZIP
                  </button>

                  {/* Download ZIP */}
                  <button className="btn btn-sm" disabled={reportStats.generated === 0}
                    onClick={handleDownloadZipClick}
                    style={{
                      ...actionBtn(128),
                      background: reportStats.generated === 0 ? "#6c757d" : "linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)",
                      border: "none", color: "white",
                    }}>
                    Download ZIP
                  </button>

                  {/* Downloads Manager */}
                  <button className="btn btn-sm" onClick={() => setDownloadsOpen(true)}
                    style={{
                      ...actionBtn(116),
                      background: activeZipJobs.length > 0
                        ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                        : "#f3f4f6",
                      // Keep the border on both states so the box never changes size.
                      border: activeZipJobs.length > 0 ? "1px solid transparent" : "1px solid #d1d5db",
                      color: activeZipJobs.length > 0 ? "white" : "#374151",
                      position: "relative", overflow: "visible",
                    }}>
                    Downloads
                    {allZipJobs.length > 0 && (
                      <span style={{
                        position: "absolute", top: -6, right: -6,
                        background: activeZipJobs.length > 0 ? "#dc2626" : "#4361ee",
                        color: "#fff", borderRadius: "50%", width: 18, height: 18,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "0.65rem", fontWeight: 700,
                      }}>
                        {allZipJobs.length}
                      </span>
                    )}
                  </button>

                  {/* Release / Hide */}
                  <button className="btn btn-sm" disabled={togglingVisibility || reportStats.generated === 0}
                    onClick={() => handleBulkVisibility(true)}
                    style={{
                      ...actionBtn(116),
                      background: togglingVisibility ? "#6c757d" : "linear-gradient(135deg, #059669 0%, #047857 100%)",
                      border: "none", color: "white",
                    }}>
                    {togglingVisibility ? "..." : `Release${countLabel}`}
                  </button>
                  <button className="btn btn-sm" disabled={togglingVisibility || reportStats.generated === 0}
                    onClick={() => handleBulkVisibility(false)}
                    style={{
                      ...actionBtn(82),
                      background: togglingVisibility ? "#6c757d" : "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                      border: "none", color: "white",
                    }}>
                    {togglingVisibility ? "..." : "Hide"}
                  </button>

                  {/* Bulk Send */}
                  <button className="btn btn-sm"
                    onClick={() => setBulkSendOpen(true)}
                    disabled={reportStats.generated === 0}
                    style={{
                      ...actionBtn(112),
                      background: reportStats.generated === 0 ? "#6c757d" : "linear-gradient(135deg, #e11d48 0%, #be123c 100%)",
                      border: "none", color: "white",
                      boxShadow: reportStats.generated === 0 ? "none" : "0 4px 12px rgba(225, 29, 72, 0.3)",
                    }}>
                    Bulk Send
                  </button>
                </div>
              </div>

              {/* Table — fixed layout with percentage widths so the extra
                  Assessment column fits on the page instead of pushing the
                  table into a horizontal scrollbar. */}
              <div className="rh-table-wrap" style={{ borderRadius: 8, border: "1px solid #e5e7eb", overflow: "hidden" }}>
                <table className="rh-table" style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                  <colgroup>
                    {colWidths.map((w, i) => <col key={i} style={{ width: w }} />)}
                  </colgroup>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      <th style={thStyle}>
                        <input type="checkbox"
                          checked={displayedRows.length > 0 && displayedRows.every((r) => selectedRowKeys.has(r.key))}
                          onChange={(e) => {
                            setSelectedRowKeys((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) displayedRows.forEach((r) => next.add(r.key));
                              else displayedRows.forEach((r) => next.delete(r.key));
                              return next;
                            });
                          }} />
                      </th>
                      <th style={thStyle}>#</th>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Username</th>
                      <th style={thStyle}>Assessment</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Completed</th>
                      <th style={thStyle}>Grade</th>
                      <th style={thStyle}>Section</th>
                      <th style={thStyle}>Report</th>
                      <th style={thStyle}>Visible</th>
                      <th style={thStyle}>Preview / Download</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Send</th>
                      {adminEditMode && (
                        <th style={{ ...thStyle, textAlign: "center" }}>Admin</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row, idx) => {
                      const s = row.student;
                      const isActionRow = row.assessment.id === Number(actionAssessmentId);
                      const globalIdx = (safeCurrentPage - 1) * pageSize + idx;
                      const secInfo = s.schoolSectionId ? sectionLookup.get(s.schoolSectionId) : undefined;
                      const asmtStatus = row.status;
                      const completedOn = formatCompletedOn(row.completedAt);
                      const rd = reportOf(row.assessment.id, s.userStudentId);
                      const vis = visibilityOf(row.assessment.id, s.userStudentId);
                      const reportStatus = rd?.reportStatus || "notGenerated";
                      const reportUrl = rd?.reportUrl || null;
                      const hasReport = reportStatus === "generated" && !!reportUrl;

                      const asc = asmtStatus === "completed" ? { bg: "#dcfce7", color: "#059669" }
                        : asmtStatus === "ongoing" ? { bg: "#dbeafe", color: "#2563eb" }
                        : { bg: "#fef3c7", color: "#d97706" };
                      const isSuppressed = reportStatus === "suppressed";
                      const rsc = hasReport ? { bg: "#dcfce7", color: "#059669" }
                        : isSuppressed ? { bg: "#fee2e2", color: "#b91c1c" }
                        : { bg: "#fef3c7", color: "#d97706" };

                      return (
                        <tr key={row.key} style={{
                          background: selectedRowKeys.has(row.key)
                            ? accentColor + "08"
                            : globalIdx % 2 === 0 ? "#fff" : "#f9fafb",
                        }}>
                          <td style={tdStyle}>
                            <input type="checkbox" checked={selectedRowKeys.has(row.key)}
                              title={isActionRow ? undefined
                                : `Bulk actions run on ${selectedAssessmentName}; this row is ${row.assessment.assessmentName}`}
                              onChange={(e) => {
                                const next = new Set(selectedRowKeys);
                                if (e.target.checked) next.add(row.key); else next.delete(row.key);
                                setSelectedRowKeys(next);
                              }} />
                          </td>
                          <td style={tdStyle}>{globalIdx + 1}</td>
                          <td style={{ ...tdWrapStyle, fontWeight: 600 }}>{s.name || "-"}</td>
                          <td style={tdStyle} title={s.username || undefined}>
                            {s.username ? (
                              <span style={{ fontFamily: "monospace", fontSize: "0.78rem" }}>{s.username}</span>
                            ) : (
                              <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: "0.8rem" }}>—</span>
                            )}
                          </td>
                          {/* The assessment this row belongs to; the acting one is marked
                              so it is obvious which rows the bulk buttons will use. */}
                          <td style={tdWrapStyle} title={row.assessment.assessmentName}>
                            <span style={{ fontWeight: isActionRow ? 600 : 400, color: isActionRow ? "#1e293b" : "#64748b" }}>
                              {row.assessment.assessmentName}
                            </span>
                            {isActionRow && shownAssessments.length > 1 && (
                              <span style={{
                                marginLeft: 5, padding: "1px 5px", borderRadius: 4, fontSize: "0.62rem",
                                fontWeight: 700, background: accentColor + "1a", color: accentColor,
                                whiteSpace: "nowrap",
                              }}>ACTIONS</span>
                            )}
                          </td>
                          <td style={tdStyle}>{statusBadge(asc.bg, asc.color, asmtStatus)}</td>
                          <td style={tdStyle}>
                            {completedOn ? (
                              <span title={new Date(row.completedAt!).toLocaleString()}>{completedOn}</span>
                            ) : (
                              <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: "0.8rem" }}>—</span>
                            )}
                          </td>
                          <td style={tdStyle}>{gradeOf(s) || "-"}</td>
                          <td style={tdStyle} title={secInfo?.sectionName || undefined}>{secInfo?.sectionName || "-"}</td>
                          <td style={tdStyle} title={isSuppressed ? (rd?.suppressionReason || "Suppressed") : undefined}>
                            {statusBadge(rsc.bg, rsc.color, hasReport ? "Generated" : isSuppressed ? "Suppressed" : "Not Generated")}
                          </td>
                          <td style={tdStyle}>
                            {vis ? (
                              <label style={{ position: "relative", display: "inline-block", width: 36, height: 20, cursor: "pointer" }}>
                                <input type="checkbox" checked={vis.visible}
                                  onChange={() => handleToggleVisibility(row.assessment.id, s.userStudentId)}
                                  style={{ opacity: 0, width: 0, height: 0, position: "absolute" }} />
                                <span style={{
                                  position: "absolute", top: 0, left: 0, right: 0, bottom: 0, borderRadius: 10,
                                  background: vis.visible ? "#059669" : "#d1d5db",
                                  transition: "background 0.2s",
                                }}>
                                  <span style={{
                                    position: "absolute", left: vis.visible ? 18 : 2, top: 2,
                                    width: 16, height: 16, borderRadius: "50%", background: "#fff",
                                    transition: "left 0.2s", boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                                  }} />
                                </span>
                              </label>
                            ) : (
                              <span style={{ color: "#d1d5db", fontSize: "0.75rem" }}>-</span>
                            )}
                          </td>
                          <td style={tdStyle}>
                            {reportUrl ? (
                              <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                                <a href={reportUrl} target="_blank" rel="noopener noreferrer"
                                  style={{
                                    padding: "3px 7px", borderRadius: 6, fontSize: "0.7rem",
                                    fontWeight: 600, background: "#dbeafe", color: "#2563eb", textDecoration: "none",
                                  }}>
                                  View
                                </a>
                                <button
                                  disabled={downloadingStudentId === s.userStudentId || rd?.pdfStatus !== "ready" || !rd?.pdfUrl}
                                  title={rd?.pdfStatus !== "ready" ? `PDF ${rd?.pdfStatus ?? "not ready"}` : "Download PDF"}
                                  style={{
                                    padding: "3px 7px", borderRadius: 6, fontSize: "0.7rem", fontWeight: 600,
                                    background: downloadingStudentId === s.userStudentId ? "#d1d5db" : "#f0fdf4",
                                    color: downloadingStudentId === s.userStudentId ? "#6b7280" : "#059669",
                                    border: "none",
                                    cursor: (rd?.pdfStatus !== "ready" || !rd?.pdfUrl) ? "not-allowed" : "pointer",
                                    opacity: (rd?.pdfStatus !== "ready" || !rd?.pdfUrl) ? 0.6 : 1,
                                  }}
                                  onClick={async () => {
                                    if (!rd?.pdfUrl) return;
                                    setDownloadingStudentId(s.userStudentId);
                                    try {
                                      const safeName = (s.name || "student").replace(/[^a-zA-Z0-9 ]/g, "").replace(/\s+/g, "_");
                                      await downloadSpacesFile(rd.pdfUrl, `${safeName}_report.pdf`);
                                    } catch { showErrorToast("Download failed (try Preview to open directly)"); }
                                    finally { setDownloadingStudentId(null); }
                                  }}>
                                  {downloadingStudentId === s.userStudentId ? "..."
                                    : rd?.pdfStatus === "ready" ? "PDF"
                                    : rd?.pdfStatus === "failed" ? "PDF ✗" : "PDF…"}
                                </button>
                                <button
                                  disabled={downloadingStudentId === s.userStudentId}
                                  style={{
                                    padding: "3px 7px", borderRadius: 6, fontSize: "0.7rem", fontWeight: 600,
                                    background: "#f3f4f6", color: "#374151", border: "none", cursor: "pointer",
                                  }}
                                  onClick={async () => {
                                    if (!reportUrl) return;
                                    setDownloadingStudentId(s.userStudentId);
                                    try {
                                      const safeName = (s.name || "student").replace(/[^a-zA-Z0-9 ]/g, "").replace(/\s+/g, "_");
                                      await downloadSpacesFile(reportUrl, `${safeName}_report.html`);
                                    } catch { showErrorToast("Download failed"); }
                                    finally { setDownloadingStudentId(null); }
                                  }}>
                                  HTML
                                </button>
                              </div>
                            ) : (
                              <span style={{ color: "#d1d5db", fontSize: "0.75rem" }}>-</span>
                            )}
                          </td>
                          <td style={{ ...tdStyle, textAlign: "center" }}>
                            {hasReport && (
                              <div style={{ display: "flex", gap: 3, justifyContent: "center", flexWrap: "wrap" }}>
                                <button
                                  className="btn btn-light-primary btn-sm"
                                  disabled={!s.email}
                                  onClick={() => handleSendEmail(row)}
                                  title={s.email ? `Email to ${s.email}` : "No email"}
                                  style={{ padding: "3px 6px", fontSize: "0.68rem", display: "flex", alignItems: "center", gap: 2 }}>
                                  <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                  Email
                                </button>
                                <button
                                  className="btn btn-light-success btn-sm"
                                  disabled={!s.phoneNumber || sendingWhatsApp.has(s.userStudentId)}
                                  onClick={() => handleSendWhatsApp(row)}
                                  title={s.phoneNumber ? `WhatsApp to ${s.phoneNumber}` : "No phone"}
                                  style={{ padding: "3px 6px", fontSize: "0.68rem", display: "flex", alignItems: "center", gap: 2 }}>
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                  </svg>
                                  WA
                                </button>
                              </div>
                            )}
                          </td>
                          {adminEditMode && (
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <button
                                className="btn btn-light-warning btn-sm"
                                onClick={() => navigate(`/admin-assessment-edit/${row.assessment.id}/${s.userStudentId}`)}
                                title={`View & edit this student's ${row.assessment.assessmentName}`}
                                style={{ padding: "3px 8px", fontSize: "0.68rem", fontWeight: 600 }}>
                                Show
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination — right-aligned "Rows per page: [n]  x - y of z  ‹ ›" bar */}
              {displayedRows.length > 0 && (
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "flex-end",
                  marginTop: 12, gap: 14, flexWrap: "wrap",
                }}>
                  <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>Rows per page:</span>
                  <select className="form-select form-select-sm form-select-solid"
                    style={{ width: 68, fontSize: "0.8rem" }} value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
                    {[10, 25, 50, 100].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <span style={{ fontSize: "0.8rem", color: "#374151" }}>
                    {(safeCurrentPage - 1) * pageSize + 1} - {Math.min(safeCurrentPage * pageSize, displayedRows.length)} of {displayedRows.length}
                  </span>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn btn-sm btn-light" disabled={safeCurrentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} aria-label="Previous page"
                      style={{ width: 32, height: 32, borderRadius: "50%", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <i className="bi bi-chevron-left" style={{ fontSize: "0.8rem" }} />
                    </button>
                    <button className="btn btn-sm btn-light" disabled={safeCurrentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} aria-label="Next page"
                      style={{ width: 32, height: 32, borderRadius: "50%", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <i className="bi bi-chevron-right" style={{ fontSize: "0.8rem" }} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ═══════════════ MODALS ═══════════════ */}

      <MiraDesaiModal
        open={miraDesaiOpen}
        onClose={() => setMiraDesaiOpen(false)}
        generating={exportingDataExcel}
        exportingMQT={exportingMQT}
        exportingDashboard={exportingDashboard}
        exportingPsychometric={exportingPsychometric}
        exportingNavigatorPro={exportingNavigatorPro}
        isNavigatorPro={isNavigatorPro}
        onExportNavigatorProRaw={handleExportNavigatorProRaw}
        onGenerateDataExcel={handleGenerateDataExcel}
        onExportBetCoreData={handleExportBetCoreData}
        onExportDashboardSheet={handleExportDashboardSheet}
        onExportPsychometricProperties={handleExportPsychometricProperties}
        onSchoolReport={() => setSchoolReportOpen(true)}
        visibleSelectedCount={visibleSelectedCount}
        displayedCount={actionRows.length}
      />

      <SchoolReportModal
        open={schoolReportOpen}
        onClose={() => setSchoolReportOpen(false)}
        assessmentId={Number(actionAssessmentId) || 0}
        assessmentName={selectedAssessmentName}
        instituteName={selectedInstituteName}
        instituteCode={Number(selectedInstitute) || 0}
        userStudentIds={
          (() => {
            const selectedVisible = getSelectedIds();
            return selectedVisible.length > 0 ? selectedVisible : undefined;
          })()
        }
      />

      <BulkSendModal
        open={bulkSendOpen}
        onClose={() => setBulkSendOpen(false)}
        selectedCount={visibleSelectedCount}
        sendingEmail={composeSending}
        sendingWhatsApp={false}
        onBulkEmail={handleBulkEmail}
        onBulkWhatsApp={handleBulkWhatsApp}
      />

      <EmailComposeModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        studentIds={composeStudentIds}
        initialRecipients={composeRecipients}
        initialSubject={composeSubject}
        initialBody={composeBody}
        sending={composeSending}
        onSend={handleComposeSend}
      />

      {/* Downloads Modal */}
      <DownloadsModal
        open={downloadsOpen}
        onClose={() => setDownloadsOpen(false)}
        jobs={allZipJobs}
        onDelete={handleDeleteZipJob}
        deleting={deletingJobs}
      />

      {autoZipOpen && selectedInstitute !== "" && (
        <AutoZipModal
          open={autoZipOpen}
          onClose={() => setAutoZipOpen(false)}
          instituteId={Number(selectedInstitute)}
          instituteName={selectedInstituteName}
          assessments={assessments}
          defaultAssessmentIds={selectedAssessmentIds}
          onStarted={onAutoZipStarted}
        />
      )}

      {generateModalOpen && selectedAssessmentObj && (
        <GenerateReportsModal
          open={generateModalOpen}
          onClose={() => setGenerateModalOpen(false)}
          assessmentId={selectedAssessmentObj.id}
          instituteCode={selectedInstitute === "" ? undefined : Number(selectedInstitute)}
          assessmentName={selectedAssessmentName}
          templates={templates}
          initialTemplateId={selectedTemplateId}
          students={modalStudents}
          onGenerated={onModalGenerated}
        />
      )}

      {queueModalOpen && selectedAssessmentObj && (
        <GenerateQueueModal
          open={queueModalOpen}
          onClose={() => setQueueModalOpen(false)}
          assessmentId={selectedAssessmentObj.id}
          instituteCode={selectedInstitute === "" ? undefined : Number(selectedInstitute)}
          assessmentName={selectedAssessmentName}
          templates={templates}
          initialTemplateId={selectedTemplateId}
          students={modalStudents}
          onGenerated={onModalGenerated}
        />
      )}

      {/* ZIP Name Prompt Modal */}
      {zipNamePromptOpen && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 1050,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(0,0,0,0.45)",
        }} onClick={() => setZipNamePromptOpen(false)}>
          <div style={{
            background: "#fff", borderRadius: 16, width: 420, maxWidth: "90vw",
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "20px 24px", borderBottom: "1px solid #e5e7eb",
            }}>
              <h3 style={{ margin: 0, fontWeight: 700, fontSize: "1.1rem", color: "#1a1a2e" }}>
                Name your ZIP
              </h3>
              <button onClick={() => setZipNamePromptOpen(false)} style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: "1.4rem", color: "#9ca3af", lineHeight: 1, padding: 4,
              }}>&times;</button>
            </div>
            <div style={{ padding: "20px 24px" }}>
              <input
                type="text"
                className="form-control form-control-solid"
                value={zipNameInput}
                onChange={(e) => setZipNameInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && zipNameInput.trim()) {
                    setZipNamePromptOpen(false);
                    startZipJob(zipNameInput.trim());
                  }
                }}
                placeholder="e.g. Class_10_BET_Reports"
                autoFocus
              />
              <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: 6 }}>
                This name will appear in Downloads and on DO Spaces.
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                <button className="btn btn-light" onClick={() => setZipNamePromptOpen(false)}
                  style={{ borderRadius: 8 }}>
                  Cancel
                </button>
                <button className="btn" disabled={!zipNameInput.trim()}
                  onClick={() => { setZipNamePromptOpen(false); startZipJob(zipNameInput.trim()); }}
                  style={{
                    background: "linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)",
                    border: "none", borderRadius: 8, padding: "10px 24px",
                    fontWeight: 600, color: "white", fontSize: "0.9rem",
                  }}>
                  Start
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating mini-indicator for active jobs (bottom-right) */}
      {activeZipJobs.length > 0 && !downloadsOpen && (
        <div
          onClick={() => setDownloadsOpen(true)}
          style={{
            position: "fixed", bottom: 24, right: 24, zIndex: 1100,
            width: 300, background: "#fff", borderRadius: 12,
            boxShadow: "0 8px 30px rgba(0,0,0,0.15)", border: "1px solid #e5e7eb",
            overflow: "hidden", cursor: "pointer",
          }}
        >
          <div style={{
            padding: "10px 16px", background: "linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <span style={{ color: "#fff", fontWeight: 700, fontSize: "0.8rem" }}>
              {activeZipJobs.length} ZIP{activeZipJobs.length > 1 ? "s" : ""} processing
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"
              style={{ animation: "spin 1s linear infinite" }}>
              <path d="M12 2v4m0 12v4m-7.07-3.93l2.83-2.83m8.48-8.48l2.83-2.83M2 12h4m12 0h4m-3.93 7.07l-2.83-2.83M7.76 7.76L4.93 4.93" />
            </svg>
          </div>
          {activeZipJobs.slice(0, 2).map((job) => (
            <div key={job.id} style={{ padding: "8px 16px", borderBottom: "1px solid #f3f4f6" }}>
              <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#1a1a2e", marginBottom: 3,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {job.name}
              </div>
              <div style={{ height: 4, background: "#ede9fe", borderRadius: 2, overflow: "hidden" }}>
                <div style={{
                  height: "100%", borderRadius: 2, transition: "width 0.3s ease",
                  background: "linear-gradient(90deg, #7c3aed, #a855f7)",
                  width: `${job.progress}%`,
                }} />
              </div>
              <div style={{ fontSize: "0.65rem", color: "#9ca3af", marginTop: 2 }}>{job.phase}</div>
            </div>
          ))}
          <div style={{ padding: "6px 16px", fontSize: "0.7rem", color: "#7c3aed", fontWeight: 600, textAlign: "center" }}>
            Click to view all
          </div>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Navigator 360 Preview Modal */}
      {nav360Preview && actionAssessmentId !== "" && (
        <Navigator360Preview
          studentId={nav360Preview.studentId}
          assessmentId={Number(actionAssessmentId)}
          studentName={nav360Preview.studentName}
          onClose={() => setNav360Preview(null)}
        />
      )}

      {/* 4-Pager Preview Modal */}
      {fourPagerPreview && actionAssessmentId !== "" && (
        <FourPagerPreview
          studentId={fourPagerPreview.studentId}
          assessmentId={Number(actionAssessmentId)}
          studentName={fourPagerPreview.studentName}
          studentClass={fourPagerPreview.studentClass}
          schoolName={selectedInstituteName}
          onClose={() => setFourPagerPreview(null)}
        />
      )}
    </div>
  );
};

// ═══════════════════════ EMAIL TEMPLATE ═══════════════════════

function buildEmailTemplate(studentName: string, reportUrl: string, assessmentName: string): string {
  return `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"></head>
<body style="margin:0;padding:0;background:#f4f6f9;font-family:Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f6f9;padding:32px 0;">
<tr><td align="center">
<table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">
  <tr>
    <td style="background:#1a1a2e;padding:24px 32px;">
      <h1 style="color:#fff;margin:0;font-size:22px;">Career-9</h1>
    </td>
  </tr>
  <tr>
    <td style="padding:32px;">
      <p style="font-size:16px;color:#333;">Dear <strong>${studentName}</strong>,</p>
      <p style="font-size:14px;color:#555;line-height:1.6;">
        Your <strong>${assessmentName}</strong> report is ready! Click the button below to view your personalized report.
      </p>
      <div style="text-align:center;margin:28px 0;">
        <a href="${reportUrl}" style="background:#4361ee;color:#fff;padding:14px 36px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;display:inline-block;">
          View Your Report
        </a>
      </div>
      <p style="font-size:13px;color:#888;">If the button doesn't work, copy this link: <a href="${reportUrl}" style="color:#4361ee;">${reportUrl}</a></p>
    </td>
  </tr>
  <tr>
    <td style="background:#f8f9fa;padding:16px 32px;text-align:center;">
      <p style="font-size:12px;color:#999;margin:0;">Career-9 Assessment Platform</p>
    </td>
  </tr>
</table>
</td></tr></table>
</body>
</html>`;
}

export default ReportsHubPage;
